import React, { useEffect } from 'react'
import AdministrationLayout from '../../../components/layouts/AdministrationLayout'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAdminDashboardData } from '../../../store/actions/adminDashboardActions'
import { fetchApplications } from '../../../store/actions/applicationsActions'
import Loader from '../../../components/elements/Loader'
import DataTable from '../../../components/elements/DataTable'
import { tableHeadersFields, transactionTimeStamp } from '../../../utils'
import ApplicationStatus from '../../../components/elements/ApplicationStatus'
import ApplicantTableSnippet from '../../../components/partials/applications/ApplicantTableSnippet'
import { Link } from 'react-router-dom'
import ArrowIcon from '../../../components/elements/icons/ArrowIcon'
import EmptyState from '../../../components/elements/EmptyState'

const AdministrationDashboard = () => {
  const dispatch = useDispatch()
  const adminDashboardSelector = useSelector((state) => state.adminDashboard)
  const applicationsSelector = useSelector((state) => state.applications)

  useEffect(() => {
    dispatch(fetchAdminDashboardData())
    dispatch(fetchApplications('', 1, 10))
  }, [dispatch])

  // Extract data
  const staffData = adminDashboardSelector.adminDashboardData?.staff || {}
  const usersData = adminDashboardSelector.adminDashboardData?.users || {}
  const admissionsData = adminDashboardSelector.adminDashboardData?.admissions || {}
  const exeatsData = adminDashboardSelector.adminDashboardData?.exeats || {}
  const examinationsData = adminDashboardSelector.adminDashboardData?.examinations || {}
  const eventsData = adminDashboardSelector.adminDashboardData?.events || {}
  const parentsData = adminDashboardSelector.adminDashboardData?.parents || {}

  // Clean up applications data for table
  const cleanUpApplicationsData = (array) => {
    if(!array) return []
    return array.map((object) => ({
      applicant: <ApplicantTableSnippet applicant={object} />,
      applicationCode: object.applicationCode,
      class: object.class,
      status: <ApplicationStatus status={object.status} />,
      date: transactionTimeStamp(object.createdAt).date,
    }))
  }

  const applicationsColumnWidths = {
    applicant: 'w-4/12',
    applicationCode: 'w-2/12',
    class: 'w-2/12',
    status: 'w-2/12',
    date: 'w-2/12',
  }

  const tableOptions = {
    selectable: false,
    clickableRows: false,
  }

  return (
    <AdministrationLayout>
      <div className='w-full'>
        {/* Main Stats Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2'>
          {/* Staff Card */}
          <div className='bg-vcm-purple rounded-lg p-6 shadow-lg border border-vcm-purple border-opacity-20'>
            <p className='text-xs uppercase tracking-[0.2em] text-white text-opacity-80 mb-2'>Staff Members</p>
            <h2 className='text-4xl font-bold text-white font-space-grotesk mb-4'>{staffData.total || 0}</h2>
            
            <div className='space-y-2 mb-4'>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-white text-opacity-70'>Academic</span>
                <span className='text-sm font-semibold text-white font-space-grotesk'>{staffData.academic || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-white text-opacity-70'>Non-Academic</span>
                <span className='text-sm font-semibold text-white font-space-grotesk'>{staffData.nonAcademic || 0}</span>
              </div>
              <div className='flex items-center justify-between pt-2 border-t border-white border-opacity-20'>
                <span className='text-xs text-white text-opacity-70'>Offboarded</span>
                <span className='text-sm font-semibold text-white font-space-grotesk'>{staffData.offboarded || 0}</span>
              </div>
            </div>

            <Link to="/admin/college-staff" className='inline-flex items-center gap-x-2 text-xs text-white hover:text-opacity-80 transition duration-200 font-medium'>
              View all staff <ArrowIcon className="w-4 h-4" />
            </Link>
          </div>

          {/* Admissions Card */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Admissions</p>
            <h2 className='text-4xl font-bold text-vcm-purple font-space-grotesk mb-4'>{admissionsData.total || 0}</h2>
            
            <div className='space-y-2 mb-4'>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-gray-600'>Active</span>
                <span className='text-sm font-semibold text-green-600 font-space-grotesk'>{admissionsData.active || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-gray-600'>Closed</span>
                <span className='text-sm font-semibold text-gray-600 font-space-grotesk'>{admissionsData.closed || 0}</span>
              </div>
              <div className='flex items-center justify-between pt-2 border-t border-gray-200'>
                <span className='text-xs text-gray-600'>Offers</span>
                <span className='text-sm font-semibold text-vcm-purple font-space-grotesk'>{admissionsData.offers?.total || 0}</span>
              </div>
            </div>

            <Link to="/admin/administration/admissions" className='text-xs text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2'>
              View admissions <ArrowIcon className="w-4 h-4" />
            </Link>
          </div>

          {/* Exeats Card */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Exeats</p>
            <h2 className='text-4xl font-bold text-vcm-purple font-space-grotesk mb-4'>{exeatsData.total || 0}</h2>
            
            <div className='space-y-2 mb-4'>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-gray-600'>Pending</span>
                <span className='text-sm font-semibold text-orange-600 font-space-grotesk'>{exeatsData.pending || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-gray-600'>Approved</span>
                <span className='text-sm font-semibold text-green-600 font-space-grotesk'>{exeatsData.approved || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-gray-600'>Completed</span>
                <span className='text-sm font-semibold text-gray-600 font-space-grotesk'>{exeatsData.completed || 0}</span>
              </div>
            </div>

            <Link to="/admin/administration/exeats" className='text-xs text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2'>
              View exeats <ArrowIcon className="w-4 h-4" />
            </Link>
          </div>

          {/* Examinations Card */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-2'>Examinations</p>
            <h2 className='text-4xl font-bold text-vcm-purple font-space-grotesk mb-4'>{examinationsData.total || 0}</h2>
            
            <div className='space-y-2 mb-4'>
              <div className='flex items-center justify-between'>
                <span className='text-xs text-gray-600'>Upcoming</span>
                <span className='text-sm font-semibold text-vcm-purple font-space-grotesk'>{examinationsData.upcoming || 0}</span>
              </div>
            </div>

            {/* <Link to="/admin/academics/examinations" className='text-xs text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2'>
              View examinations <ArrowIcon className="w-4 h-4" />
            </Link> */}
          </div>
        </div>

        {/* Secondary Stats Row */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-2 mb-8'>
          {/* Admission Offers Breakdown */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-3'>Admission Offers</p>
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Total Offers</span>
                <span className='text-lg font-semibold text-vcm-purple font-space-grotesk'>{admissionsData.offers?.total || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Accepted</span>
                <span className='text-lg font-semibold text-green-600 font-space-grotesk'>{admissionsData.offers?.accepted || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Pending</span>
                <span className='text-lg font-semibold text-orange-600 font-space-grotesk'>{admissionsData.offers?.pending || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Declined</span>
                <span className='text-lg font-semibold text-red-600 font-space-grotesk'>{admissionsData.offers?.declined || 0}</span>
              </div>
            </div>
          </div>

          {/* Users by Type */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-3'>Users by Type</p>
            <div className='space-y-3'>
              {usersData.byType?.map((userType, index) => (
                <div key={index} className='flex items-center justify-between'>
                  <span className='text-sm text-gray-600'>{userType._id.replace(/_/g, ' ')}</span>
                  <span className='text-lg font-semibold text-vcm-purple font-space-grotesk'>{userType.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Events & Others */}
          <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
            <p className='text-xs uppercase tracking-wider text-gray-500 mb-3'>Quick Stats</p>
            <div className='space-y-3'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Upcoming Events</span>
                <span className='text-lg font-semibold text-vcm-purple font-space-grotesk'>{eventsData.upcoming || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Upcoming Holidays</span>
                <span className='text-lg font-semibold text-vcm-purple font-space-grotesk'>{eventsData.upcomingHolidays || 0}</span>
              </div>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-gray-600'>Parents (Multiple Children)</span>
                <span className='text-lg font-semibold text-vcm-purple font-space-grotesk'>{parentsData.withMultipleChildren || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Applications Section */}
        <div className='bg-white rounded-lg p-6 shadow-sm border border-gray-200'>
          <div className='flex items-center justify-between mb-4'>
            <h3 className='text-lg font-medium text-gray-800'>Recent Applications</h3>
            <Link to="/admin/administration/applications" className='text-sm text-vcm-purple hover:text-gray-600 transition duration-200 flex items-center gap-x-2'>
              View all applications <ArrowIcon className="w-4 h-4" />
            </Link>
          </div>
          {applicationsSelector.loadingApplications ? (
            <Loader />
          ) : applicationsSelector.applications?.applications?.length > 0 ? (
            <DataTable
              tableHeaders={tableHeadersFields(cleanUpApplicationsData(applicationsSelector.applications.applications)[0]).headers}
              tableData={cleanUpApplicationsData(applicationsSelector.applications.applications)}
              columnWidths={applicationsColumnWidths}
              columnDataStyles={{}}
              allFields={tableHeadersFields(cleanUpApplicationsData(applicationsSelector.applications.applications)[0]).fields}
              onSelectItems={()=>{}}
              tableOptions={tableOptions}
              pagination={null}
            />
          ) : (
            <EmptyState emptyStateText="No applications yet" />
          )}
        </div>
      </div>
    </AdministrationLayout>
  )
}

export default AdministrationDashboard